import { animated, useSpring } from '@react-spring/web';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';
import _ from 'lodash';

export default function Toggle({ debounceInterval, isDisabled, isEnabled, onChange, ...rest }) {
  const handleClick = _.debounce(onChange, debounceInterval);
  const change = isDisabled ? _.noop : handleClick;

  // Space scrolls the page by default, which would carry the user away from the toggle. A disabled
  // one keeps its tab stop, so it has to leave that scroll alone rather than swallow the key.
  //
  // Stopped as well as prevented: the admin's form wrapper saves on any Enter that reaches it, and
  // this onChange is debounced behind that save — so a bubbling Enter persists the value the switch
  // just left and leaves the change unsaved.
  const onKeyDown = event => {
    if (isDisabled) return;
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();
    // Held keys auto-repeat, and onChange is debounced on its trailing edge: the press fires once in
    // the pause before the repeats, then the burst arms a second change that lands after release.
    // Swallowed above rather than returned before it, so a held Enter still never reaches the form.
    if (event.repeat) return;
    change();
  };

  const styles = useSpring({
    x: isEnabled ? PILL_WIDTH - CIRCLE_RADIUS * 2 - CIRCLE_MARGIN : CIRCLE_MARGIN,
    config: { tension: 300, friction: 24 },
  });

  return (
    <Pill
      $isDisabled={isDisabled}
      $isEnabled={isEnabled}
      aria-checked={isEnabled}
      aria-disabled={isDisabled || undefined}
      aria-label={rest['aria-label']}
      aria-labelledby={rest['aria-labelledby']}
      className={rest['className']}
      data-aid={rest['data-aid']}
      data-isenabled={isEnabled}
      onClick={change}
      onKeyDown={onKeyDown}
      role="switch"
      // Stays in the tab order while disabled: dropping out would throw focus to the document.
      tabIndex={0}
    >
      <Circle $isDisabled={isDisabled} as={animated.div} style={styles} />
    </Pill>
  );
}

Toggle.propTypes = {
  /** Time (in milliseconds) to delay multiple click interactions (prevents repeated clicking) */
  debounceInterval: PropTypes.number,
  isDisabled: PropTypes.bool,
  isEnabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string,
  'data-aid': PropTypes.string,
};

Toggle.defaultProps = {
  debounceInterval: 100,
  isDisabled: false,
  isEnabled: false,
};

const CIRCLE_MARGIN = 2;
const CIRCLE_RADIUS = 10;
const PILL_WIDTH = 48;

const pillEnabled = css`
  background-color: ${p => p.theme.colors.green400};
`;

const Pill = styled.div`
  background-color: ${p => (!p.$isDisabled ? p.theme.colors.gray400 : p.theme.colors.gray200)};
  border-radius: 12px;
  cursor: pointer;
  height: 24px;
  position: relative;
  transition: background-color 300ms ease;
  width: 48px;
  ${p => p.$isEnabled && pillEnabled};

  &:focus-visible {
    outline: 2px solid ${p => p.theme.colors.green400};
    outline-offset: 2px;
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`;

const Circle = styled.div`
  background: ${p => (!p.$isDisabled ? p.theme.colors.white : p.theme.colors.gray300)};
  border-radius: 10px;
  box-shadow: var(--color-box-shadow-default);
  height: ${CIRCLE_RADIUS * 2}px;
  position: absolute;
  top: ${CIRCLE_MARGIN}px;
  width: ${CIRCLE_RADIUS * 2}px;
`;
